import * as React from 'react';
import { connect } from 'react-redux';
import { AppStates } from '../../reducers/app';
import { UIEventListeners } from '../../../../../common/src/ts/types/uievent';
import { registerUIEventListeners } from '../../../../../common/src/ts/utils/uievent';
import * as xmlns from '../../../../../common/src/ts/xmlns';
import { getGlobalFrameStore } from '../../services/FrameStore';
import { FrameInfo, isArkWindow } from '../../../../../common/src/ts/types/FrameInfo';
import * as uuidv4 from 'uuid/v4';

export interface PageIFrameOwnProps {
  type: 'file' | 'xhtml';
  content: string;
  frameInfo: FrameInfo;
  width?: number;
  height?: number;
  style: React.CSSProperties;
  pointerListeners?: UIEventListeners;
}

interface PageIFrameProps extends PageIFrameOwnProps {
  ignorePointerEvents: boolean;
}

const blankXhtml = 'blank.xhtml';

export class PageIFrame extends React.PureComponent<PageIFrameProps, {}> {
  private readonly pointerEvents: UIEventListeners = {
    onPointerDown: (e) => {
      const { props: { pointerListeners } } = this;
      if (pointerListeners !== undefined) {
        return pointerListeners.onPointerDown(e);
      }
    },
    onPointerUp: (e) => {
      const { props: { pointerListeners } } = this;
      if (pointerListeners !== undefined) {
        return pointerListeners.onPointerUp(e);
      }
    },
    onPointerMove: (e) => {
      const { props: { pointerListeners } } = this;
      if (pointerListeners !== undefined) {
        return pointerListeners.onPointerMove(e);
      }
    },
    onGestureStart: (e) => {
      const { props: { pointerListeners } } = this;
      if (pointerListeners !== undefined) {
        return pointerListeners.onGestureStart(e);
      }
    },
    onGestureEnd: (e) => {
      const { props: { pointerListeners } } = this;
      if (pointerListeners !== undefined) {
        return pointerListeners.onGestureEnd(e);
      }
    },
    onGestureChange: (e) => {
      const { props: { pointerListeners } } = this;
      if (pointerListeners !== undefined) {
        return pointerListeners.onGestureChange(e);
      }
    },
  };

  private evaluateGeneratedContentScripts(doc: Document) {
    const documentElement = doc.documentElement;
    const head = doc.head;

    if (documentElement == null || head == null) {
      return;
    }

    // Find <script> elements of generated content.
    const scriptElements = documentElement.querySelectorAll('script');

    for (let i = 0; i < scriptElements.length; i += 1) {
      const elm = scriptElements[i];

      // Evaluate scripts
      const evaluator = doc.createElement('script');

      if (elm.textContent !== null && elm.textContent.length > 0) {
        evaluator.textContent = elm.textContent;
        head.appendChild(evaluator);
        head.removeChild(evaluator);
      } else {
        const src = elm.getAttribute('src');
        if (src !== null) {
          evaluator.setAttribute('src', src);
          head.appendChild(evaluator);
          head.removeChild(evaluator);
        }
      }
    }
  }

  private handleLoad = (e: React.SyntheticEvent<HTMLIFrameElement>) => {
    const doc = e.currentTarget.contentDocument;
    if (doc === null) {
      throw Error('Page loading failure. Prease check network and reload this application.');
    }

    if (this.props.type === 'xhtml' && doc.documentElement !== null) {
      doc.documentElement.innerHTML = this.props.content;
      this.evaluateGeneratedContentScripts(doc);
    }

    this.addUIEventHandersToDoc(doc);

    // For SVG embedded by <object> element.
    const object = doc.getElementsByTagNameNS(xmlns.html, 'object')[0];
    if (object !== undefined) {
      this.addUIEventHandlersToSVGObject(object as HTMLObjectElement);
    }

    // TODO : SVG content documentには未対応
  }

  private addUIEventHandersToDoc(doc: Document): void {
    registerUIEventListeners(doc, this.pointerEvents);
  }

  private addUIEventHandlersToSVGObject(object: HTMLObjectElement, first: boolean = true): void {
    let svgDoc: Document | null = null;
    try {
      // Safari causes "NotSupportedError" on loading, while most browsers just return "null".
      svgDoc = object.getSVGDocument();
    } catch (e) { /* ignore */ }

    if (svgDoc !== null) {
      this.addUIEventHandersToDoc(svgDoc);
    } else if (first) {
      object.addEventListener('load', () => this.addUIEventHandlersToSVGObject(object, false));
    } else {
      console.warn('Failed to inject viewer script: failed to get SVG document element.');
    }
  }

  private handleRef = (el: HTMLIFrameElement | null): void => {
    const frameStore = getGlobalFrameStore();
    const { frameInfo } = this.props;

    if (el === null) {
      const ref = frameStore.getRef(frameInfo.frameID);
      if (ref === undefined) {
        // 削除済み
        return;
      }

      const iframeWindow = ref.contentDocument && ref.contentDocument.defaultView;
      if (iframeWindow == null || !(isArkWindow(iframeWindow))) {
        // 空frameやArkWindowでない(injectされていない)不正なframeは削除
        frameStore.unregisterFrame(frameInfo.frameID);
        return;
      }

      if (ref.src.split('/').splice(-1)[0] === blankXhtml) {
        // srcがblank.xhtml(動的生成)の場合は削除
        frameStore.unregisterFrame(frameInfo.frameID);
        return;
      }

      // frameを削除
      // ref.src !== this.props.content の時は
      // この後同一frameIDのframeを再利用(replaceContent)するので消さない
      if (ref.src === this.props.content) {
        frameStore.unregisterFrame(frameInfo.frameID);
      }
    } else {
      frameStore.registerFrame(frameInfo, el);
    }
  }

  public render() {
    const baseStyle: React.CSSProperties = {
      width: '100%',
      height: '100%',
      border: 'none',
      display: 'block',
      pointerEvents: 'initial',
    };
    if (this.props.width !== undefined && this.props.height !== undefined) {
      baseStyle.width = `${this.props.width}px`;
      baseStyle.height = `${this.props.height}px`;
    }
    if (this.props.ignorePointerEvents) {
      baseStyle.pointerEvents = 'none';
    }

    let src: string;
    if (this.props.type === 'file') {
      src = this.props.content;
    } else {
      // Unfortunately, IE11 does not support blob URL and data URL for iframe "src".
      // const blob = new Blob([popupInfo.xhtml], { type: 'application/xhtml+xml' });
      // src = URL.createObjectURL(blob);
      src = blankXhtml;
    }

    return <iframe
      id={uuidv4()} // Workaround against IE/Edge bug. See issue #16.
      src={src}
      key={src}
      style={{ ...baseStyle, ...this.props.style }}
      ref={this.handleRef}
      onLoad={this.handleLoad} />;
  }
}

const mapStateToProps = (state: AppStates) => {
  return {
    ignorePointerEvents: state.uimode.pagesIgnorePointerEvents > 0,
  };
};

export default connect(mapStateToProps)(PageIFrame);
